import { useEffect, useState } from "react";
import { Calendar as CalIcon, ExternalLink } from "lucide-react";
import api from "../lib/api";
import { BRAND_COLORS, STATUS_COLORS, withAlpha } from "../lib/bookingStatus";
import { deDateMediumShortYear } from "../lib/dateFormat";

// 20 Jun 2026 · Datei-Split von RequestDetailModal.jsx. Selbständige,
// präsentationale Teil-Komponenten (verknüpfte-Buchung-Chip, Meta-Zeile,
// Zuständigen-Zweizeiler) 1:1 verbatim ausgelagert — Logik unverändert.

// Feb 2026 · User-Directive „Anfrage-Buchungs-Verlinkung im UI":
// Kompakter, klickbarer Chip für die verknüpfte Buchung. Zeigt Datum +
// Objekt-Name statt einer nichtssagenden UUID; Klick springt via
// `?open=<booking_id>` in die Bookings-Liste, die das Detail-Modal
// automatisch für diese Buchung öffnet.
function BookingLinkChip({ booking, bookingId }) {
  const short = (bookingId || "").slice(0, 8);
  const label = (() => {
    if (!booking) return `Buchung ${short}…`;
    const fmt = (iso) => {
      if (!iso) return "?";
      try {
        return deDateMediumShortYear(iso);
      } catch { return iso; }
    };
    const range = `${fmt(booking.from_date)} → ${fmt(booking.to_date)}`;
    const prop = booking.property_name ? ` · ${booking.property_name}` : "";
    return `${range}${prop}`;
  })();
  const cancelled = booking?.status === "cancelled";
  const href = `/team/bookings?open=${encodeURIComponent(bookingId)}`;
  return (
    <a
      href={href}
      data-testid="request-detail-booking-chip"
      data-booking-id={bookingId}
      title="Zur Buchung springen"
      className="group inline-flex items-center gap-2 max-w-full pl-3 pr-2 py-1.5 border rounded-full text-[12.5px] transition-colors hover:border-gold"
      style={{
        background: withAlpha(BRAND_COLORS.gold, 0.06),
        borderColor: withAlpha(BRAND_COLORS.gold, 0.35),
        color: BRAND_COLORS.navy,
        textDecoration: cancelled ? "line-through" : "none",
      }}
    >
      <CalIcon className="w-3.5 h-3.5 shrink-0" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.6} />
      <span className="truncate">{label}</span>
      {cancelled && (
        <span
          className="label-caps text-[9px] px-1.5 py-0.5 rounded-full ml-1"
          style={{ background: withAlpha(STATUS_COLORS.red, 0.15), color: STATUS_COLORS.red }}
        >
          Storniert
        </span>
      )}
      <ExternalLink
        className="w-3 h-3 opacity-60 group-hover:opacity-100 shrink-0"
        style={{ color: BRAND_COLORS.gold }}
        strokeWidth={1.8}
      />
    </a>
  );
}

function MetaRow({ icon: Icon, label, children, testid }) {
  if (children === null || children === undefined || children === "" || children === false) return null;
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-navy/5 last:border-b-0" data-testid={testid}>
      <Icon className="w-4 h-4 mt-0.5 shrink-0" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.6} />
      <div className="flex-1 min-w-0">
        <div className="label-caps text-navy-dark/55 mb-0.5">{label}</div>
        <div className="text-[13.5px] text-navy leading-relaxed break-words">{children}</div>
      </div>
    </div>
  );
}

/**
 * Feb 2026 · 11 Sep 2026 · Header-Zweizeiler mit den beiden Zuständigen.
 * User-Directive: „Zweizeiler in Anfrage-Detail spiegeln: Zeige Haupt-
 * Concierge und Verantwortlichen auch im Sonderwünsche-/Requests-Detail-
 * Modal für volle UX-Konsistenz." — 1:1 gespiegelt aus
 * `BookingHeaderAssigneeLines` (BookingDetailModal.jsx L51) und
 * `ProjectHeaderAssigneeLines` (TeamProjectDetail.jsx).
 *
 * Datenquelle: `request.assigned_concierge_id` (Backend enriched via
 * Property-Fallback, siehe requests_router.py::list_requests) +
 * `request.assigned_to` (direkte Anfrage-Zuweisung). Namen kommen live
 * aus `/team/members`.
 */
function RequestHeaderAssigneeLines({ request }) {
  const [members, setMembers] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/team/members");
        setMembers((data || []).filter((u) => ["admin", "concierge"].includes(u.role)));
      } catch { /* silent — Namen fallen nur auf "nicht zugewiesen" zurück */ }
    })();
  }, []);
  const nameOf = (id) => {
    if (!id) return null;
    const m = members.find((x) => x.id === id);
    return m?.name || m?.email || null;
  };
  const hauptId = request?.assigned_concierge_id || null;
  const respId = request?.assigned_to || null;
  const hauptName = nameOf(hauptId);
  const respName = nameOf(respId);
  const bothSameId = hauptId && respId && hauptId === respId;
  return (
    <div className="mt-2 flex flex-col gap-0.5" data-testid="request-header-assignee-lines">
      <div className="text-[11.5px] leading-tight" data-testid="request-header-haupt-concierge">
        <span className="tracking-[0.14em] uppercase text-navy-dark/50 mr-1">Haupt-Concierge · </span>
        {hauptName ? (
          <span className="text-navy-dark/85">{hauptName}</span>
        ) : (
          <span className="text-navy-dark/45 italic">— (nicht zugewiesen)</span>
        )}
      </div>
      <div className="text-[11.5px] leading-tight" data-testid="request-header-responsible">
        <span className="tracking-[0.14em] uppercase text-navy-dark/50 mr-1">Verantwortlich · </span>
        {respName ? (
          <span className="text-navy-dark/85">
            {respName}
            {bothSameId && (
              <span className="ml-1.5 text-[10px] tracking-[0.14em] uppercase text-navy-dark/40"
                    data-testid="request-header-responsible-same-as-haupt">
                (identisch)
              </span>
            )}
          </span>
        ) : hauptName ? (
          <span className="text-navy-dark/70">
            {hauptName}
            <span className="ml-1.5 text-[10px] tracking-[0.14em] uppercase text-navy-dark/40"
                  data-testid="request-header-responsible-via-property">
              (via Objekt)
            </span>
          </span>
        ) : (
          <span className="text-navy-dark/45 italic">— (nicht zugewiesen)</span>
        )}
      </div>
    </div>
  );
}

export { BookingLinkChip, MetaRow, RequestHeaderAssigneeLines };
